// src/Components/Common/Notification/notificationMeta.ts
// Helper dùng chung cho NotificationDropdown (header) và trang quản lý thông báo
// Theo bảng chuẩn hoá loại thông báo trong .features/thong-bao/ke_hoach_ui_man_thong_bao.md

import { NotificationType, NotificationPriority } from '../../../slices/notifications/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('component_notification_meta');

export interface NotificationTypeMeta {
  icon: string;
  iconBg: string;
  iconColor: string;
  badgeBg: string;
  badgeColor: string;
  label: string;
}

// Xác nhận (1) và Yêu cầu (5) dùng chung 1 style (nhóm "Xác nhận/Yêu cầu" theo kế hoạch UI)
const TYPE_META: Record<number, NotificationTypeMeta> = {
  [NotificationType.Urgent]: {
    icon: 'bx bx-error-circle',
    iconBg: '#fee2e2',
    iconColor: '#dc2626',
    badgeBg: '#fee2e2',
    badgeColor: '#dc2626',
    label: st('khan_cap_label'),
  },
  [NotificationType.Warning]: {
    icon: 'bx bx-error',
    iconBg: '#fef3c7',
    iconColor: '#d97706',
    badgeBg: '#fef3c7',
    badgeColor: '#d97706',
    label: st('canh_bao_label'),
  },
  [NotificationType.Confirmation]: {
    icon: 'bx bx-check-shield',
    iconBg: '#dbeafe',
    iconColor: '#2563eb',
    badgeBg: '#dbeafe',
    badgeColor: '#2563eb',
    label: st('xac_nhan_label'),
  },
  [NotificationType.Request]: {
    icon: 'bx bx-task',
    iconBg: '#dbeafe',
    iconColor: '#2563eb',
    badgeBg: '#dbeafe',
    badgeColor: '#2563eb',
    label: st('yeu_cau_label'),
  },
  [NotificationType.Info]: {
    icon: 'bx bx-info-circle',
    iconBg: '#f0fdf4',
    iconColor: '#16a34a',
    badgeBg: '#f3f4f6',
    badgeColor: '#6b7280',
    label: st('thong_tin_label'),
  },
};

const DEFAULT_TYPE_META: NotificationTypeMeta = {
  icon: 'bx bx-bell',
  iconBg: '#f3f4f6',
  iconColor: '#6b7280',
  badgeBg: '#f3f4f6',
  badgeColor: '#6b7280',
  label: st('thong_bao_label'),
};

export const getNotificationTypeMeta = (type: NotificationType | number): NotificationTypeMeta =>
  TYPE_META[type] || DEFAULT_TYPE_META;

export const isHighPriority = (priority: NotificationPriority | number): boolean =>
  priority === NotificationPriority.High;

export const isUrgentPriority = (priority: NotificationPriority | number): boolean =>
  priority === NotificationPriority.Urgent;

/**
 * Định dạng thời gian tương đối kiểu tiếng Việt (vd: "48 phút trước", "2 giờ trước").
 * Với mốc trên 7 ngày, trả về ngày/tháng/năm cho dễ đọc.
 */
export const formatRelativeTime = (isoDate: string): string => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return st('vua_xong_text');

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return st('diff_min_phut_truoc_text', { diff_min: diffMin });

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return st('diff_hour_gio_truoc_text', { diff_hour: diffHour });

  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return st('diff_day_ngay_truoc_text', { diff_day: diffDay });

  return date.toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Định dạng ngày giờ cố định kiểu dd/mm/yyyy HH:mm (dùng cho ngày tạo và thời gian đọc
 * trong trang quản lý thông báo, nơi cần mốc thời gian rõ ràng thay vì tương đối).
 */
export const formatFullDateTime = (isoDate: string): string => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
